/* eslint-disable no-nested-ternary */
import { useContext, useEffect, useState } from 'react';

import { Button, ModalConfirmation, Typography } from 'design_system/src';

import { ReactComponent as Check } from 'design_system/src/static/icons/checkmark.svg';
import '../../../ozoner.scss';

import {
	validationItem,
	validationItemState,
} from 'components/hocs/messages/messages';
import { RequireRole } from 'components/layouts/main/sidebar/Sidebar';
import {
	cancelAppointment,
	createDocuments,
	updateCreditLineFinancialForm,
	updateFinancialForm,
} from 'models/financial-form/controllers/financial-form.controller';
import {
	FinancialFormTypeEnum,
	FormStatus,
	VehiclePurposeEnum,
	VehiclePurposeValues,
} from 'models/financial-form/enums/financial-form.enums';
import { TaxData } from 'models/financial-form/interfaces/financial-form.interfaces';
import { fetchCurrentTile } from 'models/geozon/controllers/tile.controller';
import { VehicleEvidenceDocumentType } from 'models/inventory/enums/vehicle.enum';
import { LoanOffer } from 'models/loan-offer/controllers/loan-offer.controller';
import { PaymentLinkType } from 'models/openpay/enums/openpay.enums';
import { isPaymentLinkCompleted } from 'models/openpay/interfaces/openpay.interface';
import { Roles } from 'models/ozonio/enums/ozonio.enum';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { WhitelistNameEnum } from 'models/whitelist/interfaces/whitelist.interface';
import { momentLocal } from 'models/shared/helpers/dates';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import {
	priceFormatFinancial,
	priceMXNTotal,
} from 'models/shared/helpers/prices';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { EvidenceDocumentType } from 'models/shared/types/documents';
import { getGenericMotoImageUrl } from 'models/upload/publicStorage';
import { ZapsignDocumentStatus } from 'models/zapsign/enums/zapsign.enum';
import { ozonerContext } from '../../OzonerContext';
import { CollectionResponsible } from './Segmentations';
import { ScheduleAction, VerifyAction } from './SolicitudActions';
import {
	MaxFinancingAmountInput,
	MaxPeriodicPaymentInput,
	CreditLineDownPayment,
	GenericOpenpayLink,
	LoanIdPayjoyInput,
	ValidateOfferAction,
} from './SolicitudNumberInputs';
import { SkuSection } from './SkuSection';
import {
	useVehicleDataCompleteness,
	VehicleDataValidation,
} from './VehicleDataValidation';
import ZapsignSection from './ZapsignSection';
import { useValidOffer } from './useValidOffer';

const isRfcDataComplete = (taxData?: TaxData): boolean => {
	if (!taxData) return false;

	const { rfc, zipCode, rfcName, regimeDescription, regimeCode } = taxData;

	return !!(rfc && zipCode && rfcName && regimeDescription && regimeCode);
};

function ScheduledSection() {
	const {
		ozoner,
		financialForm,
		financialForms,
		ozonios,
		vehicle,
		handleDetailsVehicle,
		initializeContext,
		setTab,
		updateFormData,
		paymentLinks,
		zapsignDocuments,
		updateZapsignDocuments,
	} = useContext(ozonerContext);

	const isCreditLinePayjoy =
		financialForm.form.type === FinancialFormTypeEnum.creditLine;

	const loanForm = financialForm.form as any;
	const isPayJoyEmployeesWhitelist =
		loanForm.whitelistName === WhitelistNameEnum.payJoyEmployees;
	const loanDurationSku: number | undefined =
		loanForm.loanApplication?.loanDuration;

	const offer = loanForm.offer as LoanOffer | undefined;
	const selectedOffer: LoanOffer | undefined =
		offer && offer.duration === loanDurationSku ? offer : undefined;

	const displayPaymentWeek = selectedOffer?.periodicPayment;
	const displayLoanAmount = selectedOffer
		? selectedOffer.periodicPayment * selectedOffer.duration
		: undefined;

	const downPayment = isCreditLinePayjoy
		? (financialForm.form as any).downPayment
		: financialForm.form.loanApplication?.downPayment;

	const { validOffer } = useValidOffer();

	const handleCreditLineUpdateDocs = () => {
		createDocuments(financialForm.form._id)
			.then((newForm) => {
				updateFormData(newForm);
				updateZapsignDocuments(false);
				toasterNotification({
					msg: 'Documentos actualizados',
					toasterType: toasterOptions.success,
					style: 'light',
				});
			})
			.catch(commonErrorHandler);
	};

	const [hasTile, setHasTile] = useState(false);
	const [hasLoanTime, setHasLoanTime] = useState(false);
	const [hasContract, setHasContract] = useState(false);
	const [hasAddress, setHasAddress] = useState(false);
	const [hasPromissory, setHasPromissory] = useState(false);
	const [hasDelivery, setHasDelivery] = useState(false);
	const [hasDownPayment, setHasDownPayment] = useState(false);
	const [isWallmart, setIsWallmart] = useState(false);
	const [hasTaxData, setTaxData] = useState(false);
	const [hasCSF, setHasCSF] = useState(false);
	const [hasIneFront, setHasIneFront] = useState(false);
	const [hasIneBack, setHasIneBack] = useState(false);
	const [hasVehicleOriginalInvoice, setVehicleOriginalInvoice] =
		useState(false);
	const [canCreateDocs, setCanCreateDocs] = useState(false);
	const [canCreateLoan, setCanCreateLoan] = useState(false);

	const [deliverOzonio, setDeliverOzonio] = useState('');
	const [openActivate, setOpenActivate] = useState(false);

	const { hasVehicleDataComplete } = useVehicleDataCompleteness(vehicle);

	const [loading, setLoading] = useState(false);
	const [loadingCancelAppointment, setLoadingCancelAppointment] =
		useState(false);

	useEffect(() => {
		setHasDownPayment(false);
		paymentLinks.forEach((link) => {
			if (
				link.paymentLinkType === PaymentLinkType.DownPayment &&
				isPaymentLinkCompleted(link.status)
			) {
				setHasDownPayment(true);
			}
		});
	}, [financialForm, financialForms, paymentLinks]);
	useEffect(() => {
		setHasContract(false);
		const contractId =
			financialForm.form.loanApplication?.zapsignContractId ??
			(financialForm.form as any).zapsignContractId;
		if (
			contractId &&
			zapsignDocuments[contractId]?.status === ZapsignDocumentStatus.SIGNED
		)
			setHasContract(true);
	}, [financialForm, financialForms, zapsignDocuments]);

	useEffect(() => {
		setHasDelivery(false);
		setHasPromissory(false);
		setHasCSF(false);
		setTaxData(isRfcDataComplete((financialForm?.form as any)?.taxData));
		const validateWallmart = /^WMT\d+$/.test(
			financialForm.vehicle?.internalId ?? '',
		);
		setIsWallmart(validateWallmart);

		financialForm?.form?.documents?.forEach((doc) => {
			if (doc.typeEvidence === EvidenceDocumentType.promissory_note) {
				setHasPromissory(true);
			}
			if (doc.typeEvidence === EvidenceDocumentType.delivery_photo) {
				setHasDelivery(true);
			}
			if (doc.typeEvidence === EvidenceDocumentType.tax_situation) {
				setHasCSF(true);
			}
		});

		setDeliverOzonio(
			ozonios[
				financialForm?.form?.loanApplication?.appointment?.deliverOzonio ?? ''
			]?.name ?? '',
		);
	}, [financialForm, financialForms, ozoner]);
	useEffect(() => {
		setVehicleOriginalInvoice(false);
		let value = false;

		financialForm?.vehicle?.documents?.forEach((doc) => {
			if (doc.typeEvidence === VehicleEvidenceDocumentType.original_invoice) {
				value = true;
			}
		});
		setVehicleOriginalInvoice(value);
	}, [financialForm, financialForms, ozoner]);

	useEffect(() => {
		setHasAddress(false);
		setHasIneFront(false);
		setHasIneBack(false);
		ozoner?.documents?.forEach((doc) => {
			if (doc.typeEvidence === EvidenceDocumentType.proof_of_address) {
				setHasAddress(true);
			}
			if (doc.typeEvidence === EvidenceDocumentType.ine_front) {
				setHasIneFront(true);
			}
			if (doc.typeEvidence === EvidenceDocumentType.ine_back) {
				setHasIneBack(true);
			}
		});
	}, [ozoner]);

	useEffect(() => {
		if (vehicle?.internalId) {
			fetchCurrentTile(vehicle.internalId).then((dev) => {
				setHasTile(!!dev?.hasDevice);
			});
		}
	}, [vehicle]);

	useEffect(() => {
		const isCLP = financialForm.form.type === FinancialFormTypeEnum.creditLine;
		setHasLoanTime(
			isCLP
				? !!(financialForm.form as any).loanDuration
				: !!financialForm.form.loanApplication?.loanDuration,
		);
		setCanCreateDocs(
			isCLP
				? hasTile &&
						(isPayJoyEmployeesWhitelist ||
							!!(financialForm.form as any).loanIdPayjoy) &&
						hasTaxData &&
						hasCSF &&
						hasIneFront &&
						hasIneBack
				: hasTile &&
						hasAddress &&
						!!financialForm?.form?.loanApplication?.isDataVerified &&
						hasVehicleOriginalInvoice &&
						hasTaxData,
		);
		setCanCreateLoan(
			hasContract &&
				hasDelivery &&
				(!downPayment || hasDownPayment) &&
				(isCLP ? hasTile : hasPromissory) &&
				(isCLP ? !!(financialForm.form as any).collectionResponsible : true) &&
				!!(financialForm.form as any).vehiclePurpose,
		);
	}, [
		hasTile,
		hasAddress,
		hasContract,
		hasPromissory,
		hasDelivery,
		hasDownPayment,
		isWallmart,
		financialForm,
		financialForms,
		hasVehicleOriginalInvoice,
		hasTaxData,
		hasCSF,
		hasIneFront,
		hasIneBack,
		hasLoanTime,
	]);

	const activateContract = () => {
		setLoading(true);
		const isCL = financialForm.form.type === FinancialFormTypeEnum.creditLine;
		const activatePromise = isCL
			? updateCreditLineFinancialForm(financialForm.form._id, {
					status: FormStatus.completed,
			  })
			: updateFinancialForm(financialForm.form._id, {
					status: FormStatus.completed,
			  });
		activatePromise
			.then(() => {
				toasterNotification({
					msg: 'Credito activado correctamente',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				initializeContext();
				setOpenActivate(false);
				setLoading(false);
				setTab(4);
			})
			.catch((err) => {
				setLoading(false);
				commonErrorHandler(err);
			});
	};

	const goToApproved = () => {
		setLoadingCancelAppointment(true);
		(financialForm.form.loanApplication?.appointment
			? cancelAppointment(financialForm.form._id)
			: Promise.resolve()
		)
			.then(() => {
				return isCreditLinePayjoy
					? updateCreditLineFinancialForm(financialForm.form._id, {
							status: FormStatus.approved,
					  })
					: updateFinancialForm(financialForm.form._id, {
							status: FormStatus.approved,
					  });
			})
			.then((newForm) => {
				setLoadingCancelAppointment(false);
				updateFormData(newForm);
				setLoading(false);
				toasterNotification({
					msg: 'Solicitud Exitosa',
					style: 'light',
					toasterType: toasterOptions.success,
				});
			})
			.catch((err) => {
				setLoadingCancelAppointment(false);
				commonErrorHandler(err);
				setLoading(false);
			});
	};

	return (
		<>
			<div
				id="validate-soli-ozoner"
				className="p_md border_b_solid border_1 border_neutral_400 display_flex flex_col"
			>
				<Typography scale="medium" weight="400" className="m_b_xs">
					Resumen de credito
				</Typography>
				<div className="display_flex">
					<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
						<Typography
							scale="xxsmall"
							weight="400"
							textColor="neutral_600"
							className="text_no_break text_center m_b_xxs"
						>
							Enganche
						</Typography>
						<Typography
							scale="small"
							weight="600"
							textColor="secondary_500"
							className="text_no_break text_center"
						>
							{priceFormatFinancial(downPayment ?? 0, 0)}
						</Typography>
					</div>
				</div>
				{!isCreditLinePayjoy && (
					<>
						<div className="display_flex">
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Tiempo
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="neutral_1000"
									className="text_no_break text_center"
								>
									{financialForm?.form?.loanApplication?.loanDuration} Semanas
								</Typography>
							</div>
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Cuota
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="primary_300"
									className="text_no_break text_center"
								>
									{displayPaymentWeek
										? priceFormatFinancial(displayPaymentWeek)
										: '--'}
								</Typography>
							</div>
						</div>
						<div className="display_flex">
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Total crédito
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="green_300"
									className="text_no_break text_center"
								>
									{displayLoanAmount
										? priceFormatFinancial(displayLoanAmount)
										: '--'}
								</Typography>
							</div>
						</div>
						<div className="display_flex ">
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Alianza
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="neutral_900"
									className="text_no_break text_center"
								>
									{financialForm?.form?.loanApplication?.partnership ?? '--'}
								</Typography>
							</div>
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Segmentacion Crédito
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="neutral_900"
									className="text_no_break text_center"
								>
									{financialForm?.form?.loanApplication?.loanSegmentation}
								</Typography>
							</div>
						</div>
					</>
				)}
				{isCreditLinePayjoy && (
					<>
						<div className="display_flex">
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Tiempo
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="neutral_1000"
									className="text_no_break text_center"
								>
									{offer?.duration ?? '--'} Semanas
								</Typography>
							</div>
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Cuota
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="primary_300"
									className="text_no_break text_center"
								>
									{offer?.periodicPayment
										? priceFormatFinancial(offer?.periodicPayment)
										: '--'}
								</Typography>
							</div>
						</div>
						<div className="display_flex">
							<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
									className="text_no_break text_center m_b_xxs"
								>
									Total crédito
								</Typography>
								<Typography
									scale="small"
									weight="600"
									textColor="green_300"
									className="text_no_break text_center"
								>
									{offer?.totalExtraAmount && offer?.financingAmount
										? priceFormatFinancial(
												offer.totalExtraAmount + offer.financingAmount,
										  )
										: '--'}
								</Typography>
							</div>
						</div>
					</>
				)}
				{isCreditLinePayjoy && !isPayJoyEmployeesWhitelist && (
					<div className="display_flex">
						<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
							<Typography
								scale="xxsmall"
								weight="400"
								textColor="neutral_600"
								className="text_no_break text_center m_b_xxs"
							>
								ID Crédito PayJoy
							</Typography>
							<Typography
								scale="small"
								weight="600"
								textColor="neutral_900"
								className="text_no_break text_center"
							>
								{(financialForm?.form as any)?.loanIdPayjoy ?? '--'}
							</Typography>
						</div>
					</div>
				)}
				{isCreditLinePayjoy && (
					<div className="display_flex">
						<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
							<Typography
								scale="xxsmall"
								weight="400"
								textColor="neutral_600"
								className="text_no_break text_center m_b_xxs"
							>
								Pago semanal máximo
							</Typography>
							<Typography
								scale="small"
								weight="600"
								textColor="neutral_900"
								className="text_no_break text_center"
							>
								{(financialForm?.form as any)?.maxPeriodicPayment !== undefined
									? priceMXNTotal(
											(financialForm.form as any).maxPeriodicPayment,
											2,
									  )
									: '      -- MXN'}
							</Typography>
						</div>
					</div>
				)}
				{isCreditLinePayjoy && isPayJoyEmployeesWhitelist && (
					<div className="display_flex">
						<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
							<Typography
								scale="xxsmall"
								weight="400"
								textColor="neutral_600"
								className="text_no_break text_center m_b_xxs"
							>
								Monto máximo a financiar
							</Typography>
							<Typography
								scale="small"
								weight="600"
								textColor="neutral_900"
								className="text_no_break text_center"
							>
								{(financialForm?.form as any)?.maxFinancingAmount !== undefined
									? priceMXNTotal(
											(financialForm.form as any).maxFinancingAmount,
											2,
									  )
									: '      -- MXN'}
							</Typography>
						</div>
					</div>
				)}
				{vehicle ? (
					<div className="display_flex flex_gap_md flex_align_center border_solid border_1 border_neutral_400 p_xs">
						<div className="br_xs overflow_hidden foto_moto">
							<img
								src={
									vehicle.images[0]
										? vehicle.images[0].url
										: getGenericMotoImageUrl()
								}
								alt="moto detail"
							/>
						</div>
						<div className="flex_grow_1 flex_center_col">
							<Typography
								scale="small"
								weight="600"
								className="inventory_link cursor_pointer"
								onClick={(e) => handleDetailsVehicle(e, vehicle.internalId)}
							>
								{vehicle.internalId}
							</Typography>

							<Typography scale="xxsmall" weight="400" className="m_b_xxs">
								{vehicle.brand && vehicle.model
									? `${vehicle.brand.name} ${vehicle.model.name} ${
											vehicle.suffix ?? ''
									  }`
									: 'no definido'}
							</Typography>

							<Typography scale="xsmall" weight="400" className="m_b_xxs">
								{vehicle.providerSku}
							</Typography>

							<Typography scale="xsmall" weight="400" className="m_b_xxs">
								{priceFormatFinancial(vehicle.marketPrice)}
							</Typography>
						</div>
					</div>
				) : null}
				<div className="display_flex">
					<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
						<Typography
							scale="xxsmall"
							weight="400"
							textColor="neutral_600"
							className="text_no_break text_center m_b_xxs"
						>
							Uso de la moto
						</Typography>
						{offer?.commercialUse === undefined ? (
							<Typography
								scale="small"
								weight="600"
								textColor="neutral_900"
								className="text_no_break text_center"
							>
								--
							</Typography>
						) : (
							<Typography
								scale="small"
								weight="600"
								textColor="neutral_900"
								className="text_no_break text_center"
							>
								{offer?.commercialUse
									? VehiclePurposeValues[VehiclePurposeEnum.comercial]
									: VehiclePurposeValues[VehiclePurposeEnum.personal]}
							</Typography>
						)}
					</div>
				</div>
				<div className="display_flex">
					<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
						<Typography
							scale="xxsmall"
							weight="400"
							textColor="neutral_600"
							className="text_no_break text_center m_b_xxs"
						>
							Cita de entrega
						</Typography>
						<Typography
							scale="small"
							weight="600"
							textColor="neutral_900"
							className="text_no_break text_center"
						>
							{momentLocal(
								financialForm?.form?.loanApplication?.appointment?.scheduledAt,
							).format('YYYY-MM-DD hh:mm A')}
						</Typography>
					</div>
				</div>
				<div className="display_flex m_b_md">
					<div className="flex_grow_1 flex_basis_0 border_solid border_1 border_neutral_400 p_y_xxs">
						<Typography
							scale="xxsmall"
							weight="400"
							textColor="neutral_600"
							className="text_no_break text_center m_b_xxs"
						>
							Encargado de entrega
						</Typography>
						<Typography
							scale="small"
							weight="600"
							textColor="neutral_900"
							className="text_no_break text_center"
						>
							{deliverOzonio}
						</Typography>
					</div>
				</div>

				{!isCreditLinePayjoy && <ScheduleAction disable={false} />}
				{(!isCreditLinePayjoy ||
					financialForm.form.status === FormStatus.scheduled) && (
					<Button
						scale="small"
						color="grey"
						className="m_t_md"
						disabled={loadingCancelAppointment}
						onClick={() => goToApproved()}
					>
						Cancelar Entrega
					</Button>
				)}
			</div>

			<ValidateOfferAction />

			{isCreditLinePayjoy && !isPayJoyEmployeesWhitelist && (
				<LoanIdPayjoyInput />
			)}
			{isCreditLinePayjoy && (
				<RequireRole authRoles={[Roles.ADMIN, Roles.COLLECTOR_MANAGER]}>
					<div className="p_md border_b_solid border_1 border_neutral_400">
						<CollectionResponsible />
					</div>
				</RequireRole>
			)}
			{downPayment ? (
				<GenericOpenpayLink type={PaymentLinkType.DownPayment} />
			) : null}

			{!isCreditLinePayjoy && (
				<RequireRole authRoles={[Roles.ADMIN, Roles.COLLECTOR_MANAGER]}>
					<div
						id="verify-soli-ozoner"
						className="p_md border_b_solid border_1 border_neutral_400 display_flex flex_col flex_gap_md"
					>
						{financialForm?.form?.loanApplication?.isDataVerified ? (
							<div className="flex_center flex_gap_xs">
								<Typography
									scale="small"
									weight="600"
									textColor="green_300"
									className="text_no_break text_center"
								>
									Credito Verificado
								</Typography>
								<Check className="text_green_300 dim_md" />
							</div>
						) : (
							<>
								<div className="display_flex flex_gap_xs flex_align_center">
									{validationItem(
										financialForm?.form?.loanApplication?.collectionResponsible
											? validationItemState.valid
											: validationItemState.invalid,
										{
											[validationItemState.valid]:
												'Responsable de cobranza asignado',
											[validationItemState.invalid]:
												'Responsable de cobranza sin asignar',
										},
									)}
								</div>
								<div className="display_flex flex_gap_xs flex_align_center">
									{validationItem(
										financialForm?.form?.loanApplication?.collectionSegmentation
											? validationItemState.valid
											: validationItemState.invalid,
										{
											[validationItemState.valid]:
												'Segmentacion de cobranza asignada',
											[validationItemState.invalid]:
												'Segmentacion de cobranza sin asignar',
										},
									)}
								</div>
								<VerifyAction
									disabled={
										!financialForm?.form?.loanApplication
											?.collectionResponsible ||
										!financialForm?.form?.loanApplication
											?.collectionSegmentation
									}
								/>
							</>
						)}
					</div>
				</RequireRole>
			)}

			<div
				id="validate-soli-ozoner"
				className="p_md border_b_solid border_1 border_neutral_400 display_flex flex_col flex_gap_md"
			>
				{!isCreditLinePayjoy && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							financialForm?.form?.loanApplication?.isDataVerified
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'Verificacion de cobranza hecha',
								[validationItemState.invalid]:
									'Verificacion de cobranza pendiente',
							},
						)}
					</div>
				)}
				{isCreditLinePayjoy && (
					<>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								vehicle
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]: 'Moto asignada',
									[validationItemState.invalid]: 'Moto sin asignar',
								},
							)}
						</div>
						{!isCreditLinePayjoy && (
							<div className="display_flex flex_gap_xs flex_align_center">
								{validationItem(
									hasLoanTime
										? validationItemState.valid
										: validationItemState.invalid,
									{
										[validationItemState.valid]: 'Plan de semanas seleccionado',
										[validationItemState.invalid]:
											'Plan de semanas sin seleccionar',
									},
								)}
							</div>
						)}
						{!isPayJoyEmployeesWhitelist && (
							<div className="display_flex flex_gap_xs flex_align_center">
								{validationItem(
									(financialForm?.form as any)?.loanIdPayjoy
										? validationItemState.valid
										: validationItemState.invalid,
									{
										[validationItemState.valid]: 'ID Crédito PayJoy asignado',
										[validationItemState.invalid]:
											'ID Crédito PayJoy sin asignar',
									},
								)}
							</div>
						)}
						{downPayment ? (
							<div className="display_flex flex_gap_xs flex_align_center">
								{validationItem(
									hasDownPayment
										? validationItemState.valid
										: validationItemState.invalid,
									{
										[validationItemState.valid]: 'Enganche pagado',
										[validationItemState.invalid]: 'Enganche pendiente',
									},
								)}
							</div>
						) : null}
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(validationItemState.valid, {
								[validationItemState.valid]: 'Segmentación: Credit Line PayJoy',
								[validationItemState.invalid]: 'Segmentación sin asignar',
							})}
						</div>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								(financialForm?.form as any)?.collectionResponsible
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]:
										'Responsable de cobranza asignado',
									[validationItemState.invalid]:
										'Responsable de cobranza sin asignar',
								},
							)}
						</div>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								hasCSF
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]: 'CSF (Situación Fiscal) adjunto',
									[validationItemState.invalid]:
										'CSF (Situación Fiscal) sin subir',
								},
							)}
						</div>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								hasIneFront
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]: 'INE Frontal adjunto',
									[validationItemState.invalid]: 'INE Frontal sin subir',
								},
							)}
						</div>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								hasIneBack
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]: 'INE Posterior adjunto',
									[validationItemState.invalid]: 'INE Posterior sin subir',
								},
							)}
						</div>
					</>
				)}
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						hasTile ? validationItemState.valid : validationItemState.invalid,
						{
							[validationItemState.valid]: 'Vehículo con Tile',
							[validationItemState.invalid]: 'Vehículo sin Tile asignado',
						},
					)}
				</div>
				{!isCreditLinePayjoy && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							hasAddress
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'Comprobante Domicilio adjunto',
								[validationItemState.invalid]:
									'Comprobante Domicilio sin subir',
							},
						)}
					</div>
				)}
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						hasTaxData
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Informacion Fiscal Correcta',
							[validationItemState.invalid]: 'Informacion Fiscal Incompleta',
						},
					)}
				</div>
				{!isCreditLinePayjoy && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							hasVehicleOriginalInvoice
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'Factura de Origen lista',
								[validationItemState.invalid]: 'Factura de Origen faltante',
							},
						)}
					</div>
				)}
			</div>
			<div
				id="validate-soli-ozoner"
				className="p_md border_b_solid border_1 border_neutral_400"
			>
				<ZapsignSection
					canCreateDocs={canCreateDocs}
					contractId={
						isCreditLinePayjoy
							? (financialForm.form as any).zapsignContractId
							: undefined
					}
					onUpdateDocs={
						isCreditLinePayjoy ? handleCreditLineUpdateDocs : undefined
					}
				/>
			</div>
			<VehicleDataValidation />
			<div
				id="validate-soli-ozoner"
				className="p_md border_b_solid border_1 border_neutral_400 display_flex flex_col flex_gap_md"
			>
				{downPayment ? (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							hasDownPayment
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'Enganche pagado',
								[validationItemState.invalid]: 'Enganche pendiente',
							},
						)}
					</div>
				) : null}
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						hasPromissory
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Pagare adjunto',
							[validationItemState.invalid]: 'Pagare sin subir',
						},
					)}
				</div>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						hasContract
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Contrato firmado',
							[validationItemState.invalid]: 'Contrato sin firmar',
						},
					)}
				</div>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						hasDelivery
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Foto entrega adjunto',
							[validationItemState.invalid]: 'Foto entrega sin subir',
						},
					)}
				</div>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						(financialForm.form as any).vehiclePurpose
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Uso de la moto asignado',
							[validationItemState.invalid]: 'Uso de la moto sin asignar',
						},
					)}
				</div>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						validOffer
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Oferta valida',
							[validationItemState.invalid]: 'Ofeerta invalida',
						},
					)}
				</div>
				<Button
					scale="small"
					color="primary"
					disabled={!canCreateLoan || !canCreateDocs || !hasVehicleDataComplete}
					onClick={() => setOpenActivate(true)}
				>
					Entregar Moto
				</Button>
			</div>

			{openActivate && (
				<ModalConfirmation
					openConfirmation={openActivate}
					handleClose={() => setOpenActivate(false)}
					icon={<Check className="text_green_300" />}
					widthDesktop={40}
					isConfirmationLoading={false}
					actions={[
						{
							label: 'Volver',
							color: 'grey',
							action: () => setOpenActivate(false),
						},
						{
							label: 'Activar credito',
							color: 'primary',
							action: activateContract,
							disabled: loading,
						},
					]}
					title={
						<Typography scale="large" weight="400">
							¿Estás seguro de que{' '}
							<Typography scale="large" weight="600" component="span">
								deseas activar este credito?
							</Typography>
						</Typography>
					}
					subtitle={
						<Typography scale="medium" weight="400" textColor="neutral_700">
							Esta accion se debe realizar una vez se haya entregado la moto y
							se hayan firmado todos los documentos pertinentes
						</Typography>
					}
				/>
			)}
		</>
	);
}
export default ScheduledSection;
